import { useEffect, useState } from 'react'
import { Alert, Button, Input, Modal, Select, Space, Spin, Typography } from 'antd'
import type { AgentBuilderDashboard } from '../types'
import { parseDashboardDefinitionJson } from '../coerceDashboardDefinition'
import { getDashboardSectionOptions, isSecuredAgentBuilderPlatform, normalizeDashboardSection, sanitizeDashboardSection } from '../sections'

type PlatformOption = { value: number; label: string }

type Props = {
  open: boolean
  dashboard: AgentBuilderDashboard | null
  platformOptions: PlatformOption[]
  platformsLoading: boolean
  saving: boolean
  onClose: () => void
  onSave: (values: { platformTypeId: number; section: string | null }) => Promise<void>
}

function resolveDashboardSection(dashboard: AgentBuilderDashboard | null) {
  if (!dashboard) return null

  if (dashboard.section) {
    return sanitizeDashboardSection(dashboard.section, dashboard.platformTypeId)
  }

  const parsed = parseDashboardDefinitionJson(dashboard.dashboardJson)
  const section = parsed?.section ?? null
  return sanitizeDashboardSection(section, dashboard.platformTypeId)
}

export function EditAgentBuilderDashboardModal({
  open,
  dashboard,
  platformOptions,
  platformsLoading,
  saving,
  onClose,
  onSave,
}: Props) {
  const [platformTypeId, setPlatformTypeId] = useState<number>(0)
  const [section, setSection] = useState<string | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open || !dashboard) return
    setSection(resolveDashboardSection(dashboard))
    setError('')
  }, [open, dashboard])

  useEffect(() => {
    if (!open || !dashboard || platformsLoading) return
    const dashboardPlatformId = dashboard.platformTypeId
    const isAvailable = platformOptions.some((option) => option.value === dashboardPlatformId)
    setPlatformTypeId(isAvailable ? dashboardPlatformId : (platformOptions[0]?.value ?? 0))
  }, [open, dashboard, platformOptions, platformsLoading])

  function onPlatformChange(nextPlatformTypeId: number) {
    setPlatformTypeId(nextPlatformTypeId)
    // Each secured platform supports a different subset of sections, so drop a
    // selection the newly chosen platform cannot use.
    setSection((current) => (
      isSecuredAgentBuilderPlatform(nextPlatformTypeId)
        ? normalizeDashboardSection(current, nextPlatformTypeId)
        : null
    ))
  }

  function close() {
    if (saving) return
    onClose()
  }

  async function handleSave() {
    if (isSecuredAgentBuilderPlatform(platformTypeId) && !section) {
      setError('A dashboard section is required for this platform.')
      return
    }

    setError('')
    try {
      await onSave({
        platformTypeId,
        section: sanitizeDashboardSection(section, platformTypeId),
      })
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'Unable to save dashboard.')
    }
  }

  return (
    <Modal
      title="Edit Dashboard"
      open={open}
      onCancel={close}
      closable={!saving}
      maskClosable={!saving}
      keyboard={!saving}
      footer={[
        <Button key="cancel" onClick={close} disabled={saving}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={saving} disabled={saving} onClick={() => void handleSave()}>
          Save
        </Button>,
      ]}
    >
      <Spin spinning={saving}>
        <Space direction="vertical" size={16} className="w-full">
          {error ? <Alert type="error" showIcon message={error} /> : null}

          <label className="flex flex-col gap-1">
            <Typography.Text type="secondary">Title</Typography.Text>
            <Input value={dashboard?.title || 'Untitled Dashboard'} disabled />
          </label>

          <label className="flex flex-col gap-1">
            <Typography.Text type="secondary">Platform</Typography.Text>
            <Select
              value={platformTypeId || undefined}
              options={platformOptions}
              onChange={onPlatformChange}
              loading={platformsLoading}
              disabled={saving}
            />
          </label>

          {isSecuredAgentBuilderPlatform(platformTypeId) ? (
            <label className="flex flex-col gap-1">
              <Typography.Text type="secondary">Dashboard Section</Typography.Text>
              <Select
                value={section ?? undefined}
                options={getDashboardSectionOptions(platformTypeId)}
                onChange={setSection}
                placeholder="Select section"
                disabled={saving}
              />
            </label>
          ) : null}
        </Space>
      </Spin>
    </Modal>
  )
}

export default EditAgentBuilderDashboardModal
